import React, { useMemo, useState } from 'react'
import { ArrowLeft, CheckCircle, AlertTriangle, X, Trash2, Link2Off, Split as SplitIcon, Plus } from 'lucide-react'
import {
  projectsApi,
  AiSuggestionRow,
  AcceptSuggestionError,
  ProjectSystem,
} from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'
import { SplitSuggestionModal }      from '@/components/projects/SplitSuggestionModal'
import { CreateProjectSystemModal }  from '@/components/projects/CreateProjectSystemModal'

interface Props {
  raDocumentId: string
  // project the suggestions live under. Phase 3.5 threads this to the
  // CreateProjectSystemModal so suggest-function can read the project's
  // own taxonomy as the candidate list
  projectId:    string
  canEdit:      boolean
  // persisted pending suggestions, refetched on every sub-tab activation
  // by the parent. Phase E1 passed the live session status here. Phase
  // E1.5 reads from the DB instead, so suggestions survive page nav,
  // refresh, and container restart
  suggestions:       AiSuggestionRow[]
  projectSystemsById: Map<string, ProjectSystem>
  onBack:     () => void   // back to the mappings sub-tab
  onChanged:  () => void   // any per-row or bulk action. parent refetches
}

type FilterMode = 'all' | 'high' | 'medium' | 'low' | 'no_match'

const CONFIDENCE_LABEL: Record<AiSuggestionRow['confidence'], { label: string; cls: string; icon: React.ComponentType<{ className?: string }> }> = {
  high:   { label: 'high',   cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: CheckCircle },
  medium: { label: 'medium', cls: 'bg-amber-100   text-amber-700   dark:bg-amber-900/40   dark:text-amber-300',   icon: AlertTriangle },
  low:    { label: 'low',    cls: 'bg-slate-100   text-slate-600   dark:bg-slate-700      dark:text-slate-300',   icon: AlertTriangle },
}

export const AutoMapReviewPane: React.FC<Props> = ({
  raDocumentId, projectId, canEdit, suggestions, projectSystemsById, onBack, onChanged,
}) => {
  const { error: showError, success, showToast } = useToast()

  // selection for batch-accept. defaults to high-confidence rows with a
  // non-null suggested ps AND no target_conflict. "no good match" rows
  // are never selectable since acceptance writes the
  // suggested_project_system_id and null means nothing-to-do.
  // target_conflict rows would 409 against uq_ra_sa_psid_per_doc, so we
  // exclude them up front rather than let bulk-accept partial-fail with a
  // confusing "skipped" count
  const initialSelected = useMemo(() => {
    const m: Record<string, boolean> = {}
    for (const s of suggestions) {
      m[s.id] = s.confidence === 'high'
                && !!s.suggested_project_system_id
                && !s.target_conflict
    }
    return m
  }, [suggestions])

  const [selected,   setSelected]   = useState<Record<string, boolean>>(initialSelected)
  const [filter,     setFilter]     = useState<FilterMode>('all')
  const [working,    setWorking]    = useState<Record<string, boolean>>({})  // per-row spinner gate
  const [bulkBusy,   setBulkBusy]   = useState<null | 'accept' | 'clear'>(null)
  const [confirmClear, setConfirmClear] = useState(false)
  // Phase 3 Word RA authority modals. splitTarget holds the suggestion
  // being split (drives SplitSuggestionModal). createTarget holds the
  // suggestion being CREATEd from (drives CreateProjectSystemModal). both
  // also trigger the 409 toast's [Split…] / [Create…] action buttons
  const [splitTarget,  setSplitTarget]  = useState<AiSuggestionRow | null>(null)
  const [createTarget, setCreateTarget] = useState<AiSuggestionRow | null>(null)

  const counts = useMemo(() => {
    const c = { all: suggestions.length, high: 0, medium: 0, low: 0, no_match: 0 }
    for (const s of suggestions) {
      if (!s.suggested_project_system_id) c.no_match++
      else                                c[s.confidence]++
    }
    return c
  }, [suggestions])

  const visible = useMemo(() => {
    return suggestions.filter(s => {
      if (filter === 'all')      return true
      if (filter === 'no_match') return !s.suggested_project_system_id
      return s.suggested_project_system_id && s.confidence === filter
    })
  }, [suggestions, filter])

  const acceptableCount = useMemo(() =>
    canEdit ? suggestions.filter(s => s.suggested_project_system_id && selected[s.id]).length : 0
  , [canEdit, suggestions, selected])

  const handleAcceptSelected = async () => {
    const targets = suggestions.filter(s =>
      s.suggested_project_system_id && !s.target_conflict && selected[s.id]
    )
    if (targets.length === 0) {
      showError('Select at least one suggestion to accept.')
      return
    }
    setBulkBusy('accept')
    let ok      = 0
    let skipped = 0
    let failed  = 0
    // sequential per-row accept. /accept runs each in its own txn, so one
    // 409 (e.g. ASSESSMENT_SLOT_TAKEN from a stale listing, or
    // SUGGESTION_ALREADY_ACTED if someone else acted in the meantime)
    // doesn't abort the others. bucket them into "skipped" and surface the
    // partial-success summary. 409s with a known code are skips, not failures
    for (const s of targets) {
      try {
        await projectsApi.acceptAiSuggestion(s.id)
        ok++
      } catch (err) {
        const e = err as AcceptSuggestionError
        if (e.code === 'ASSESSMENT_SLOT_TAKEN' || e.code === 'SUGGESTION_ALREADY_ACTED') {
          skipped++
        } else {
          failed++
        }
      }
    }
    setBulkBusy(null)
    if (ok > 0)      success(`${ok} suggestion${ok === 1 ? '' : 's'} accepted${skipped > 0 ? `, ${skipped} skipped (conflict or already actioned)` : ''}`)
    if (failed > 0)  showError(`${failed} suggestion${failed === 1 ? '' : 's'} failed to accept`)
    // nudge any open System Detail Word RA tab to refetch
    window.dispatchEvent(new CustomEvent('seacuredb:ra-mappings-updated'))
    onChanged()
  }

  const handleRowAction = async (id: string, action: 'accept' | 'reject' | 'dismiss') => {
    setWorking(w => ({ ...w, [id]: true }))
    try {
      if      (action === 'accept')  await projectsApi.acceptAiSuggestion(id)
      else if (action === 'reject')  await projectsApi.rejectAiSuggestion(id)
      else                           await projectsApi.dismissAiSuggestion(id)
      if (action === 'accept') {
        window.dispatchEvent(new CustomEvent('seacuredb:ra-mappings-updated'))
      }
      onChanged()
    } catch (err) {
      if (action === 'accept') {
        const e = err as AcceptSuggestionError
        if (e.code === 'ASSESSMENT_SLOT_TAKEN') {
          // Phase 3: toast carries a [Split…] button that opens the same
          // SplitSuggestionModal as the per-row button. find the suggestion
          // we just tried to accept so the modal has full row context. if
          // it's gone from the local listing (race), fall back to a plain
          // toast + refetch
          const stale = suggestions.find(x => x.id === id)
          if (stale && stale.suggested_project_system_id) {
            showToast({
              type:    'error',
              title:   'Already mapped',
              message: e.message,
              action:  { label: 'Split…', onClick: () => setSplitTarget(stale) },
            })
          } else {
            showError(e.message)
          }
          onChanged()
          return
        }
        if (e.code === 'SUGGESTION_ALREADY_ACTED') {
          // someone else accepted/rejected/dismissed in another tab.
          // refetch to drop the stale row
          showError('This suggestion was already actioned — refreshing list.')
          onChanged()
          return
        }
      }
      showError((err as Error)?.message ?? `Failed to ${action} suggestion`)
    } finally {
      setWorking(w => ({ ...w, [id]: false }))
    }
  }

  const handleClearAll = async () => {
    setBulkBusy('clear')
    setConfirmClear(false)
    try {
      const result = await projectsApi.clearAllAiSuggestions(raDocumentId)
      success(`Cleared ${result.cleared} pending suggestion${result.cleared === 1 ? '' : 's'}`)
      onChanged()
    } catch (err: any) {
      showError(err?.message ?? 'Failed to clear pending suggestions')
    } finally {
      setBulkBusy(null)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
        <button onClick={onBack} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">AI Suggestions</h2>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">
            {suggestions.length} pending suggestion{suggestions.length === 1 ? '' : 's'}
          </div>
        </div>
        {canEdit && suggestions.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmClear(true)}
            disabled={bulkBusy !== null}
            title="Mark every pending suggestion as dismissed"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Clear all pending
          </Button>
        )}
      </div>

      <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2 flex-wrap">
        <select
          value={filter}
          onChange={e => setFilter(e.target.value as FilterMode)}
          className="text-xs border border-slate-300 dark:border-slate-600 dark:bg-slate-800 rounded px-2 py-1.5"
        >
          <option value="all">All ({counts.all})</option>
          <option value="high">High ({counts.high})</option>
          <option value="medium">Medium ({counts.medium})</option>
          <option value="low">Low ({counts.low})</option>
          <option value="no_match">No match ({counts.no_match})</option>
        </select>
        <span className="text-[11px] text-slate-500 ml-auto">{acceptableCount} selected</span>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {suggestions.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-400">
            No pending suggestions. Start an Auto-map run from the Mappings sub-tab.
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-400">No suggestions in this filter.</div>
        ) : visible.map(s => {
          const ps  = s.suggested_project_system_id ? projectSystemsById.get(s.suggested_project_system_id) : null
          const conf = CONFIDENCE_LABEL[s.confidence]
          const Icon = conf.icon
          const noMatch = !s.suggested_project_system_id
          const hasConflict = !!s.target_conflict
          const isChecked = !!selected[s.id]
          const rowBusy = !!working[s.id] || bulkBusy === 'accept' || bulkBusy === 'clear'
          // same wording the server returns on a SLOT_TAKEN 409. kept
          // FE-side so the disabled-Accept tooltip explains the conflict
          // before the click, not only on the error toast
          const conflictMessage = hasConflict
            ? `${ps?.system_name ?? 'That project system'} is already mapped to "${s.target_conflict}" in this document. Use Split to create a separate inventory row, or pick a different row in the Mappings tab.`
            : ''
          return (
            <div key={s.id} className="border border-slate-200 dark:border-slate-700 rounded p-2.5 flex gap-2.5 items-start">
              <input
                type="checkbox"
                checked={canEdit && isChecked}
                disabled={!canEdit || noMatch || hasConflict || rowBusy}
                onChange={e => setSelected({ ...selected, [s.id]: e.target.checked })}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                    {s.assessment_system_name ?? '(unknown system)'}
                  </span>
                  {s.assessment_supplier && <span className="text-[10px] text-slate-500">{s.assessment_supplier}</span>}
                </div>
                <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap">
                  <span className="text-slate-400">→</span>
                  {noMatch ? (
                    <span className="italic text-slate-400">(no good match found)</span>
                  ) : (
                    <>
                      <span className="font-medium text-slate-700 dark:text-slate-200">
                        {ps?.system_name ?? '(unknown project system)'}
                      </span>
                      {ps?.vendor_name_raw && (
                        <span className="text-slate-500 text-[10px]">{ps.vendor_name_raw.slice(0, 30)}</span>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${conf.cls}`}>
                    <Icon className="h-3 w-3" />
                    {conf.label}
                  </span>
                  {hasConflict && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                      title={conflictMessage}
                    >
                      <Link2Off className="h-3 w-3" />
                      Conflicts with: {s.target_conflict}
                    </span>
                  )}
                  <span className="text-[11px] text-slate-600 dark:text-slate-300 leading-snug">
                    “{s.reasoning}”
                  </span>
                </div>
                {s.tools_used.length > 0 && (
                  <div className="text-[10px] text-slate-400 mt-1">
                    Tools: {Array.from(new Set(s.tools_used)).join(', ')}
                  </div>
                )}
              </div>
              {canEdit && <div className="flex flex-col gap-1 shrink-0">
                {!noMatch && !hasConflict && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRowAction(s.id, 'accept')}
                    disabled={rowBusy}
                    title="Apply this mapping to ra_system_assessments"
                  >
                    Accept
                  </Button>
                )}
                {/* Phase 3: conflicted rows get [Split…] instead of Accept.
                    opens SplitSuggestionModal with a sensible name default
                    derived from the Word system */}
                {hasConflict && ps && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSplitTarget(s)}
                    disabled={rowBusy}
                    title={conflictMessage}
                  >
                    <SplitIcon className="h-3 w-3 mr-1" />
                    Split…
                  </Button>
                )}
                {/* Phase 3: no-match rows get [Create system…]. opens
                    CreateProjectSystemModal, which previews the field
                    mapping (verbatim name, vendor, editable function) and
                    POSTs the create endpoint */}
                {noMatch && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCreateTarget(s)}
                    disabled={rowBusy}
                    title="Create a new inventory row from this Word RA system"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Create system…
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRowAction(s.id, 'reject')}
                  disabled={rowBusy}
                  title="This match is wrong"
                >
                  Reject
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRowAction(s.id, 'dismiss')}
                  disabled={rowBusy}
                  title="Hide this suggestion without judging it"
                >
                  Dismiss
                </Button>
              </div>}
            </div>
          )
        })}
      </div>

      <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-slate-500">
          {acceptableCount === 0
            ? 'No suggestions selected'
            : `${acceptableCount} suggestion${acceptableCount === 1 ? '' : 's'} ready to accept`}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack} disabled={bulkBusy !== null}>
            Back to mappings
          </Button>
          {canEdit && (
            <Button size="sm" onClick={handleAcceptSelected} disabled={bulkBusy !== null || acceptableCount === 0}>
              {bulkBusy === 'accept' ? 'Accepting…' : `Accept selected${acceptableCount > 0 ? ` (${acceptableCount})` : ''}`}
            </Button>
          )}
        </div>
      </div>

      {canEdit && confirmClear && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-rose-500" />
              <h3 className="text-base font-semibold">Clear all pending suggestions?</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Every pending suggestion will be marked dismissed. They stay in the audit
              history but disappear from this pane. The underlying assessments are not
              changed.
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmClear(false)}>
                <X className="h-3.5 w-3.5 mr-1.5" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleClearAll}>
                Clear all pending
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 3 Word RA authority modals. both refetch on success via
          onChanged so the conflict chip disappears and the row drops out
          of pending. cross-tab notify via the same event the accept path
          uses. the System Detail Word RA tab listens */}
      {canEdit && splitTarget && splitTarget.suggested_project_system_id && (() => {
        const original = projectSystemsById.get(splitTarget.suggested_project_system_id)
        if (!original) { setSplitTarget(null); return null }
        return (
          <SplitSuggestionModal
            original={original}
            assessment={{
              id:          splitTarget.assessment_id,
              system_name: splitTarget.assessment_system_name,
              supplier:    splitTarget.assessment_supplier,
            }}
            conflictHolderName={splitTarget.target_conflict}
            onClose={() => setSplitTarget(null)}
            onSplit={() => {
              setSplitTarget(null)
              window.dispatchEvent(new CustomEvent('seacuredb:ra-mappings-updated'))
              onChanged()
            }}
          />
        )
      })()}

      {canEdit && createTarget && (
        <CreateProjectSystemModal
          projectId={projectId}
          assessment={{
            id:            createTarget.assessment_id,
            system_name:   createTarget.assessment_system_name,
            supplier:      createTarget.assessment_supplier,
            ship_function: createTarget.assessment_ship_function,
          }}
          onClose={() => setCreateTarget(null)}
          onCreated={() => {
            setCreateTarget(null)
            window.dispatchEvent(new CustomEvent('seacuredb:ra-mappings-updated'))
            onChanged()
          }}
          onMapToExisting={async (existingPsId) => {
            // run the existing mapping PATCH so the assessment binds to
            // the row we just learned about. patchRaAssessmentMapping also
            // supersedes the pending suggestion in the same txn
            try {
              await projectsApi.patchRaAssessmentMapping(createTarget.assessment_id, existingPsId)
              success('Mapped to existing inventory row')
              setCreateTarget(null)
              window.dispatchEvent(new CustomEvent('seacuredb:ra-mappings-updated'))
              onChanged()
            } catch (err: any) {
              showError(err?.message ?? 'Failed to map to existing row')
            }
          }}
        />
      )}
    </div>
  )
}
